import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable, {
  Column,
} from "../../components/common/data-table/DataTable";
import TitleUpdater from "../../components/common/title/TitleUpdater";
import styles from "./Brands.module.scss";
import DeleteModal from "../../components/common/delete-modal/DeleteModal";
import { useAuth } from "../../context/AuthContext";
import BrandsService from "../../api/services/brands.service";
import Modal from "../../components/common/modal/Modal";
import Input from "../../components/common/input/Input";
import Button from "../../components/common/button/Button";
import PlusIcon from "../../assets/images/actions/PlusIcon";
import ShowImage from "../../components/common/show-image/ShowImage";

interface Errors {
  name: string;
  image: string;
}

const Brands = () => {
  const { t } = useTranslation("translation");
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    image: null as File | null,
    preview: "" as string,
  });
  const { setButtons, searchValue } = useAuth();
  const [count, setCount] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [errors, setErrors] = useState<Partial<Errors>>({});
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>("");

  const getData = useCallback(async () => {
    if (!searchValue) setLoading(true);
    try {
      const res = await BrandsService.getAllBrands(page, limit, searchValue);
      setBrands(res.data.data);
      setCount(res.data.count);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchValue]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1) return;
    if (count && newPage > Math.ceil(count / limit)) return;
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleDelete = async (id: number) => {
    try {
      await BrandsService.deleteBrand(id);
      setDeleteModal(false);
      getData();
    } catch (error) {
      console.error(error);
    }
  };
  const validateForm = (): boolean => {
    const newErrors: Partial<Errors> = {};
    if (!formData.name?.trim())
      newErrors.name = t("brands.errors.name_required") as string;
    if (!formData.image && !formData.preview)
      newErrors.image = t("brands.errors.image_required") as string;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      const data = new FormData();
      if (formData.name) data.append("name", formData.name);
      if (formData.image) data.append("image", formData.image);

      if (editId) {
        await BrandsService.updateBrand(editId, data);
      } else {
        await BrandsService.createBrand(data);
      }

      setIsVisible(false);
      setFormData({ name: "", image: null, preview: "" });
      setEditId(null);
      getData();
    } catch (error) {
      console.error(error);
    }
  };

  const handleEdit = async (id: number) => {
    setErrors({
      name: "",
      image: "",
    });
    try {
      const res = await BrandsService.getSingleBrand(id);
      setEditId(id);
      setFormData({
        name: res.data.name,
        image: null,
        preview: res.data.image || "",
      });
      setIsVisible(true);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getData();
  }, [getData]);

  const columns: Column[] = [
    { key: "name", header: t("brands.table.brand_name") as string },
    {
      key: "image",
      header: t("brands.table.image") as string,
      render: (i) => (
        <div
          className={styles.brand_img_wrapper}
          onClick={() => {
            setSelectedImage(i);
            setShowImageModal(true);
          }}
        >
          {i ? <img src={i} alt="brand" className={styles.brand_img} /> : "-"}
        </div>
      ),
    },
  ];

  useEffect(() => {
    setButtons([
      <Button
        icon={<PlusIcon />}
        key="add"
        onClick={() => {
          setIsVisible(true);
          setErrors({
            name: "",
            image: "",
          });
        }}
      >
        {t("brands.add_button") as string}
      </Button>,
    ]);
    return () => setButtons([]);
  }, [setButtons]);

  return (
    <div className={styles.delivery}>
      <TitleUpdater title={t("brands.title") as string} />

      <DataTable
        table_title={t("brands.title") as string}
        data={brands}
        columns={columns}
        actions
        onDelete={(id) => {
          setDeleteModal(true);
          setDeleteId(id);
        }}
        onEdit={(id) => handleEdit(id)}
        other
        loading={loading}
        paginationData={{
          total: count,
          page,
          limit,
          onPageChange: handlePageChange,
          onLimitChange: handleLimitChange,
        }}
      />

      <DeleteModal
        deleteModal={deleteModal}
        onClose={() => setDeleteModal(false)}
        onSubmit={() => handleDelete(Number(deleteId))}
        title={t("brands.delete_title") as string}
      />
      <ShowImage
        imagesModal={showImageModal}
        setImagesModal={setShowImageModal}
        image={selectedImage}
      />
      <Modal
        isOpen={isVisible}
        onClose={() => {
          setIsVisible(false);
          setEditId(null);
          setFormData({ name: "", image: null, preview: "" });
        }}
        title={
          editId
            ? (t("brands.edit_title") as string)
            : (t("brands.add_button") as string)
        }
        onSubmit={handleSubmit}
      >
        <form className={styles.form}>
          <Input
            label={t("brands.form.name") as string}
            placeholder={t("brands.form.placeholder_name") as string}
            value={formData.name}
            onChange={(e) => {
              setFormData({ ...formData, name: e.target.value });
              setErrors((prev) => ({ ...prev, name: "" }));
            }}
            error={errors.name}
          />
          <div className={styles.imageInputWrapper}>
            <Input
              label={t("brands.form.image") as string}
              type="file"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setFormData({
                  ...formData,
                  image: file,
                  preview: file ? URL.createObjectURL(file) : formData.preview,
                });
                setErrors((prev) => ({ ...prev, image: "" }));
              }}
              error={errors.image}
            />

            {formData.preview && (
              <div className={styles.previewWrapper}>
                <img
                  src={formData.preview}
                  alt="preview"
                  className={styles.previewImage}
                />
              </div>
            )}
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Brands;
